import { useEffect } from "react";
import { useLocation } from "react-router-dom";

function getScrollContainer(el: HTMLElement | null) {
  // Prefer explicit marker if present
  const marked = document.querySelector<HTMLElement>("[data-sidebar-scroll]");
  if (marked) return marked;
  // Fallback: nearest scrollable ancestor
  let node: HTMLElement | null = el?.parentElement || null;
  while (node && node !== document.body) {
    const s = getComputedStyle(node);
    if (
      /(auto|scroll|overlay)/.test(s.overflowY) &&
      node.scrollHeight > node.clientHeight
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

function scrollHeaderToTop(
  header: HTMLElement,
  container: HTMLElement,
  pad = 8,
) {
  const h = header.getBoundingClientRect();
  const c = container.getBoundingClientRect();
  const top = h.top - c.top + container.scrollTop - pad;
  container.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
}

export function useScrollMenuHeaderToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    // wait for the submenu to render
    const id = requestAnimationFrame(() => {
      const active = document.querySelector<HTMLElement>(
        '[data-active-subitem="true"]',
      );
      if (!active) return;

      // find enclosing menu item & its header
      const menuItem = active.closest<HTMLElement>("[data-menu-item]");
      const header = menuItem?.querySelector<HTMLElement>("[data-menu-header]");
      if (!menuItem || !header) return;

      // If the section is collapsed, open it first (Radix sets data-state on the asChild trigger)
      const state = header.getAttribute("data-state"); // "open" | "closed" (from CollapsibleTrigger)
      if (state === "closed") {
        header.click(); // open the section
        // after it opens and content lays out, scroll the header
        setTimeout(() => {
          const container = getScrollContainer(header);
          if (container) scrollHeaderToTop(header, container, 8);
        }, 60);
        return;
      }

      // already open: just scroll header to top
      const container = getScrollContainer(header);
      if (container) scrollHeaderToTop(header, container, 8);
    });

    return () => cancelAnimationFrame(id);
  }, [pathname]);
}
